import React, { useCallback, useEffect, useRef, useState } from "react";
import { Network, Plus, Upload, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(3)}`);

export const CarriersAdmin = () => {
  const [carriers, setCarriers] = useState([]);
  const [overview, setOverview] = useState([]);
  const [form, setForm] = useState({ name: "", byoc_trunk_sid: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState("");
  const [probeRes, setProbeRes] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    const [c, o] = await Promise.all([api.get("/admin/carriers"), api.get("/admin/routes/overview")]);
    setCarriers(c.data); setOverview(o.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try { await api.post("/admin/carriers", { ...form, status: "paused" }); setForm({ name: "", byoc_trunk_sid: "", notes: "" }); await load(); toast.success("Carrier added (paused until you activate it)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add carrier"); } finally { setBusy(false); }
  };

  const save = async (c, patch) => {
    try { await api.put(`/admin/carriers/${c.id}`, { name: c.name, byoc_trunk_sid: c.byoc_trunk_sid || "", status: c.status, billing_increment: c.billing_increment || 60, notes: c.notes || "", ...patch }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} and its rate deck?`)) return;
    await api.delete(`/admin/carriers/${c.id}`); await load();
  };

  const upload = async (c, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/carriers/${c.id}/rates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} prefixes imported · ${data.countries_repriced} countries re-priced`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const runProbe = async () => {
    if (!probe.trim()) return;
    try { const { data } = await api.get("/admin/routes/preview", { params: { number: probe } }); setProbeRes(data); }
    catch { toast.error("Could not evaluate that number"); }
  };

  return (
    <div data-testid="admin-carriers" className="space-y-6">
      <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm">
        <h3 className="font-heading font-bold flex items-center gap-2 mb-1"><Network className="h-4 w-4 text-primary" /> Carriers &amp; least-cost routing</h3>
        <p className="text-sm text-zinc-500 mb-4">
          Each call picks the best route for its prefix: cost + answer rate + caller-ID delivery + recent failures. If a wholesale route fails, the call
          retries over the next route, then Twilio. User prices always follow the winning carrier&rsquo;s cost + your margin. Carriers stay <b>paused</b> until you add their Twilio BYOC trunk SID and activate them.
        </p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Carrier name (e.g. TICS, AfriCallShop, BICS)" data-testid="carrier-name-input" className="bg-zinc-50 border border-black/10 rounded-xl px-4 py-3" />
          <input value={form.byoc_trunk_sid} onChange={(e) => setForm({ ...form, byoc_trunk_sid: e.target.value })} placeholder="Twilio BYOC trunk SID (BY…) — optional for now" data-testid="carrier-byoc-input" className="bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 font-mono text-sm" />
          <button onClick={add} disabled={busy || !form.name.trim()} data-testid="carrier-add-button" className="h-12 px-5 rounded-xl bg-primary text-white font-bold flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add carrier
          </button>
        </div>
      </div>

      {carriers.length > 0 && (
        <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="carriers-table">
            <thead><tr className="text-xs uppercase tracking-wide text-zinc-400 font-bold text-left">
              <th className="p-3">Carrier</th><th className="p-3">BYOC trunk</th><th className="p-3">Rate deck</th><th className="p-3">Quality</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-black/5">
              {carriers.map((c) => (
                <tr key={c.id} data-testid={`carrier-row-${c.id}`}>
                  <td className="p-3 font-semibold">{c.name}</td>
                  <td className="p-3">
                    <input defaultValue={c.byoc_trunk_sid || ""} onBlur={(e) => e.target.value !== (c.byoc_trunk_sid || "") && save(c, { byoc_trunk_sid: e.target.value.trim() })}
                      placeholder="BY…" data-testid={`carrier-byoc-${c.id}`} className="bg-zinc-50 border border-black/10 rounded-lg px-2 py-1 font-mono text-xs w-44" />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-600">{c.prefixes ? `${c.prefixes} prefixes` : "none"}</span>
                      <input type="file" accept=".csv,text/csv" className="hidden" ref={(el) => (fileRefs.current[c.id] = el)} onChange={(e) => upload(c, e.target.files?.[0])} data-testid={`carrier-deck-input-${c.id}`} />
                      <button onClick={() => fileRefs.current[c.id]?.click()} data-testid={`carrier-deck-upload-${c.id}`} className="text-xs font-bold text-primary flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> Upload CSV</button>
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">columns: prefix, $/min, cli_guaranteed (yes/no), label</div>
                  </td>
                  <td className="p-3 text-xs text-zinc-600">
                    {c.stats.attempts ? `${Math.round((c.stats.answered / c.stats.attempts) * 100)}% answered · ${c.stats.minutes} min` : "no calls yet"}
                  </td>
                  <td className="p-3">
                    <button onClick={() => save(c, { status: c.status === "active" ? "paused" : "active" })} data-testid={`carrier-toggle-${c.id}`}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                      {c.status === "active" ? "Active" : "Paused"}
                    </button>
                  </td>
                  <td className="p-3 text-right"><button onClick={() => remove(c)} data-testid={`carrier-delete-${c.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm">
        <h4 className="font-heading font-bold mb-2">Test a number</h4>
        <div className="flex gap-2">
          <input value={probe} onChange={(e) => setProbe(e.target.value)} placeholder="+256 784 369 397" data-testid="route-probe-input" className="flex-1 bg-zinc-50 border border-black/10 rounded-xl px-4 py-2.5 font-mono" />
          <button onClick={runProbe} data-testid="route-probe-button" className="h-11 px-4 rounded-xl border border-black/10 font-semibold flex items-center gap-2"><Search className="h-4 w-4" /> Which route?</button>
        </div>
        {probeRes && (
          <ol className="mt-3 space-y-1 text-sm" data-testid="route-probe-result">
            {probeRes.routes.map((r, i) => (
              <li key={r.carrier_id} className={`flex justify-between rounded-lg px-3 py-2 ${i === 0 ? "bg-emerald-50 font-semibold" : "bg-zinc-50"}`}>
                <span>{i === 0 ? "🏆 " : `${i + 1}. `}{r.name} <span className="text-zinc-400 font-normal">prefix {r.prefix}{r.cli ? " · CLI ✓" : ""}{r.answer_rate != null ? ` · ${Math.round(r.answer_rate * 100)}% answered` : ""}</span></span>
                <span className="font-mono">{money(r.cost)}/min · score {r.score}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {overview.length > 0 && (
        <div className="bg-white border border-black/10 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 font-heading font-bold">Countries with more than one route</div>
          <table className="w-full text-sm" data-testid="routes-overview-table">
            <thead><tr className="text-xs uppercase tracking-wide text-zinc-400 font-bold text-left"><th className="p-3">Country</th><th className="p-3">Winner</th><th className="p-3">Routes (cost/min)</th></tr></thead>
            <tbody className="divide-y divide-black/5">
              {overview.map((o) => (
                <tr key={o.dial_code}><td className="p-3">{o.flag} {o.country}</td><td className="p-3 font-semibold text-emerald-700">{o.winner}</td>
                  <td className="p-3 text-xs text-zinc-600">{o.routes.map((r) => `${r.name} ${money(r.cost)}`).join(" · ")}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
